//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { memo, Fragment, useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { PATH_DASHBOARD } from '../system/constants';
// import { BASE_URL } from '../common/constants';
// import { isOnPage } from '../system/helpers/systemHelper';

import TopNavJs from './subComponents/TopNavJs';
import TopNavEmp from './subComponents/TopNavEmp';
import TopNavPublic from './subComponents/TopNavPublic';
import TopNavBlank from './subComponents/TopNavBlank';
import { BASE_URL, USER_TYPE_EMP, USER_TYPE_JS } from '../common/constants';

//---------------------------------------------------------------------
// Common components
//---------------------------------------------------------------------
import AfitdBox from '../common/elemental/AfitdBox';
import AfitdLinearProgress from '../common/elemental/AfitdLinearProgress';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles({});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, classes, appContext, isShowProgressBar } = params;
    const { isAuthenticationPending, userId, userDocExist, currentUser, authUser } = appContext;
    const { isBlank } = props;

    const isCreateAccountMode = userId && !userDocExist;
    const isAuth = !!authUser;
    const isJs = _get(currentUser, 'type') === USER_TYPE_JS;
    const isEmp = _get(currentUser, 'type') === USER_TYPE_EMP;

    const topNavJsIsHidden = !isAuth || !isJs || isBlank;
    const topNavEmpIsHidden = !isAuth || !isEmp || isBlank;
    const topNavPublicIsHidden = isAuthenticationPending || isAuth || isBlank;
    const topNavBlankHidden = !(topNavJsIsHidden && topNavEmpIsHidden && topNavPublicIsHidden) && !isBlank;
    const progressBarContainerIsHidden = isCreateAccountMode || !isShowProgressBar;

    const defns = {
        topNavJs: { isHidden: topNavJsIsHidden },
        topNavEmp: { isHidden: topNavEmpIsHidden },
        topNavPublic: { isHidden: topNavPublicIsHidden },
        topNavBlank: { isHidden: topNavBlankHidden },
        progressBarContainer: { sx: { width: '100%' }, isHidden: progressBarContainerIsHidden },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const TopNav = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { isAuthenticationPending, authUser } = appContext;
    // const layoutContext = useContext(LayoutContext);
    // const { tempSetDashboard } = layoutContext;
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const isPromptLogin = _get(location, 'state.isPromptLogin', false);
    const redirectPathname = _get(location, 'state.redirectPathname', `${BASE_URL}${PATH_DASHBOARD}`);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isShowProgressBar } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    // const isFakePublic = false; //isOnPage(location, PATH_HOME) || isOnPage(location, PATH_RESOURCE_CENTER); //|| isOnPage(location, '/', true);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // redirect if needed be
    useEffect(() => {
        // if just authenticated and there is a redirect path in the router state, redirect to that page
        //   and it will also reset the router state.
        if (authUser && isPromptLogin && redirectPathname) {
            //---------------------------------------------------------------------
            // Switch the following to determine where to redirect after the
            //   AfitdPrivateRoute checks user's authentication.
            // Note: If user explicitly clicks the Log In button, it always
            //   redirect to Dashboard default page (My Calendar).
            //---------------------------------------------------------------------
            // For the deep link to work:
            history.push(redirectPathname);
            // To ignore the deep link (redirectPathname) and always redirect to Dashboard default page (My Calendar):
            // history.push(`${BASE_URL}${PATH_DASHBOARD}`);
        }
    }, [authUser, redirectPathname, isPromptLogin, history]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ props, classes, history, appContext, isShowProgressBar });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return isAuthenticationPending ? null : (
        <Fragment>
            <TopNavJs {...defns.topNavJs} />
            <TopNavEmp {...defns.topNavEmp} />
            <TopNavPublic {...defns.topNavPublic} />
            <TopNavBlank {...defns.topNavBlank} />
            <AfitdBox {...defns.progressBarContainer}>
                <AfitdLinearProgress />
            </AfitdBox>
        </Fragment>
    );
};

export default memo(TopNav);
